import React from 'react'
import { AppBar, Box, DialogContent, DialogProps, Tab, Tabs } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import VehicleRefinementTable from 'app/vehicles/components/VehicleRefinementTable'
import { VehicleProvider } from '@cbgms/api/modules/enums'
import { useGetVehicleSuggestionsForProvider } from './useGetVehicleSuggestionsForProvider'
import { IGenericVehicle, IVehicleType } from '@cbgms/api'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogTitleWithCloseButton, Loader } from '@carsys/ui'
import RegisteredInformation from 'app/vehicles/components/RegisteredInformation'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useRegisteredInformationTable } from 'app/vehicles/helpers/useRegisteredInformationTable'
import { reduxForm } from 'redux-form'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import useVehicleProviderCountry from 'app/vehicles/helpers/useDefaultVehicleProviderCountry'
import {
  useVehicleTreeProviderForDefaultOrderAccount,
  useVehicleTreeProviderForProvider
} from 'app/vehicles/helpers/use-vehicle-tree-config'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import RemarkLabel from 'app/vehicles/components/Details/External/RemarkLabel'
import { InstanceProps, create } from 'react-modal-promise'

interface ISuggestionsDialogBase {
  open: DialogProps['open']
  onClose: () => void
  onVehicleSelect?: (vehicle: IGenericVehicle) => void
  translationKeyForHeader?: string
  makeVehicleSelectionOnSingleMatch?: boolean
}

export interface IUpdateExternalIdPromiseProps {
  vehicleUUID: string
  provider: VehicleProvider
  onVehicleSelect?: (vehicle: IGenericVehicle) => void
  translationKeyForHeader?: string
  makeVehicleSelectionOnSingleMatch?: boolean
}

interface IUpdateExternalIdWithRetrievedSuggestions extends ISuggestionsDialogBase {
  vehicleUUID: string
  provider: VehicleProvider
}

interface IUpdateExternalIdDialogProps extends ISuggestionsDialogBase {
  vehicleSuggestions?: IGenericVehicle[] | null
  provider: VehicleProvider
}

const nColumnsRegisteredInformation = 5

export const UpdateExternalIdDialogWithRetrievedSuggestions: React.FC<IUpdateExternalIdWithRetrievedSuggestions> = props => {
  const { open, vehicleUUID, provider } = props
  const [vehicleSuggestions, getVehicleSuggestions, isLoading] = useGetVehicleSuggestionsForProvider()
  const { t } = useTranslation()

  const {
    provider: vehicleInformationProvider,
    registeredInformation,
    vehiclePropertiesAsTableRows,
    isLoading: loadingRegisteredInformation
  } = useRegisteredInformationTable(
    vehicleUUID,
    true /* Force enabled, immediatly load Registered-information */,
    nColumnsRegisteredInformation
  )

  React.useEffect(() => {
    if (open) {
      if (!!vehicleUUID && vehicleUUID !== EmptyUUID) {
        getVehicleSuggestions(vehicleUUID, provider)
      }
    }
  }, [open, vehicleUUID, provider])

  if (!open) {
    return null
  }
  if (!vehicleSuggestions && !isLoading && !loadingRegisteredInformation) {
    return null
  }
  return (
    <UpdateExternalIdDialog vehicleSuggestions={vehicleSuggestions} {...props}>
      {registeredInformation && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
            {t('vehicle_registered_information.modal.header')}
          </AccordionSummary>
          <AccordionDetails>
            <RegisteredInformation provider={vehicleInformationProvider} vehiclePropertiesAsTableRows={vehiclePropertiesAsTableRows} />
          </AccordionDetails>
        </Accordion>
      )}
    </UpdateExternalIdDialog>
  )
}

export const UpdateExternalIdDialog: React.FC<IUpdateExternalIdDialogProps> = ({
  open,
  onClose,
  onVehicleSelect,
  translationKeyForHeader,
  vehicleSuggestions: suggestions,
  provider,
  ...otherProps
}) => {
  const { t } = useTranslation()
  const providerCountry = useVehicleProviderCountry()
  // we need to hold some date for the vehicle MMT tree,
  const [tempVehicle, setTempVehicle] = React.useState<Partial<IVehicleType>>({})
  // If a provider is "provided" fetch the Vehicle Tree based on the provider
  const ComponentByProvider = useVehicleTreeProviderForProvider(provider)
  // If a not provider is supplied fallback on VehicleTree based on country
  const { Component: ComponentByCountry, provider: providerByCountry } = useVehicleTreeProviderForDefaultOrderAccount()

  const titleKey = provider ? t(`vehicle.refine-vehicle-title`) : `${t(translationKeyForHeader!)} - ${providerByCountry}`

  const VehicleTreeComponent = ComponentByProvider || ComponentByCountry

  const makeVehicleSelectionOnSingleMatch = otherProps.makeVehicleSelectionOnSingleMatch || false

  const { locale } = React.useContext(LocaleContext)

  const onVehicleSelectAndClose = (suggestion: IGenericVehicle) => {
    onVehicleSelect?.(suggestion)
    onClose()
  }

  React.useEffect(() => {
    if (makeVehicleSelectionOnSingleMatch && suggestions?.length === 1) {
      onVehicleSelectAndClose(suggestions[0])
    }
  }, [makeVehicleSelectionOnSingleMatch, suggestions])

  const classes = useStyles()
  const [value, setValue] = React.useState(0)

  const handleChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue)
  }

  // Select the 2nd tab (MMT/MDM) when there are no suggestions from the provider.
  React.useEffect(() => {
    if (suggestions?.length === 0) {
      setValue(1)
    }
  }, [suggestions])

  // If we auto make a vehicle selection on the single match, we delay the opening of the modal,
  // because it could lead to the modal opening and half a sec later (after the suggestions have been loaded) closing again.
  if (open && makeVehicleSelectionOnSingleMatch && suggestions === null) {
    return <Loader loading />
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} data-testid='select-vehicle-modal'>
      <DialogTitleWithCloseButton onClose={onClose}>{titleKey}</DialogTitleWithCloseButton>
      <DialogContent>
        {otherProps.children}
        <Box className={classes.root}>
          <AppBar position='static'>
            <Tabs value={value} onChange={handleChange} aria-label='simple tabs example' indicatorColor='secondary' textColor='inherit'>
              <Tab label={t('vehicle.update-external-tab-targeted')} id='simple-tab-0' aria-controls='simple-tabpanel-0' />
              <Tab label={t('vehicle.update-external-tab-manual')} id='simple-tab-1' aria-controls='simple-tabpanel-1' />
            </Tabs>
          </AppBar>
          <TabPanel value={value} index={0}>
            {suggestions ? (
              suggestions.length > 0 && <VehicleRefinementTable vehicles={suggestions} onSelect={onVehicleSelectAndClose} hideVin />
            ) : (
              <div style={{ position: 'relative' }}>
                <Loader loading />
              </div>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <VehicleTreeComponent
              provider={provider}
              locale={locale}
              country={providerCountry}
              vehicle={tempVehicle}
              headerContent={null}
              typeSelectMode
              onSelection={v => {
                setTempVehicle(v)
                const genericVehicle = (v as IVehicleType)?.GenericVehicle
                if (genericVehicle) {
                  onVehicleSelectAndClose(genericVehicle)
                }
              }}
            />
          </TabPanel>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1, marginRight: 3 }}>
          <ChipLabel provider={provider} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const ChipLabel: React.FC<{ provider: VehicleProvider }> = ({ provider }) => {
  const { te } = useEnumTranslation()
  if (!provider) {
    return null
  }

  return (
    <Box>
      <RemarkLabel text={te('VehicleProvider', provider)} />
    </Box>
  )
}

interface TabPanelProps {
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`}>
    {value === index && <Box p={3}>{children}</Box>}
  </div>
)

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper
  }
}))

// This export is for components that don't care about the form parts and don't need the form hooked up.
export const UpdateExternalIdDialogWithoutForm = reduxForm<{}, IUpdateExternalIdDialogProps>({
  form: 'UpdateExternalIdDialog',
  initialValues: {}
})(UpdateExternalIdDialog)

export const UpdateExternalIdDialogSuggestionsWithoutForm = reduxForm<{}, IUpdateExternalIdWithRetrievedSuggestions>({
  form: 'UpdateExternalIdDialogSuggestionsWithoutForm',
  initialValues: {}
})(UpdateExternalIdDialogWithRetrievedSuggestions)

export const UpdateExternalIdDialogPromiseWrapper: React.FC<IUpdateExternalIdPromiseProps & InstanceProps<unknown>> = ({
  onResolve,
  isOpen,
  ...props
}) => <UpdateExternalIdDialogSuggestionsWithoutForm {...props} onClose={onResolve} open={isOpen} />

export const UpdateExternalIdDialogPromise = create(UpdateExternalIdDialogPromiseWrapper)
